import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from './AdminLayout';
import { Users, TrendingUp, DollarSign, Clock, ArrowRight, Tag, BarChart3, Target, CalendarClock, Camera, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAG_COLORS = {
  buyer: 'bg-blue-500/20 text-blue-400',
  seller: 'bg-green-500/20 text-green-400',
  investor: 'bg-purple-500/20 text-purple-400',
  landlord: 'bg-yellow-500/20 text-yellow-400',
  financing: 'bg-cyan-500/20 text-cyan-400',
  notary: 'bg-orange-500/20 text-orange-400',
  agent_recruit: 'bg-pink-500/20 text-pink-400',
};

const getUserRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem('adminUser') || '{}');
    return user.role || 'admin';
  } catch { return 'admin'; }
};

export const AdminDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [upcomingApts, setUpcomingApts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [agentProfile, setAgentProfile] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoRef = useRef(null);
  const role = getUserRole();
  const isAgent = role === 'agent';

  useEffect(() => {
    fetchData();
    if (isAgent) fetchAgentProfile();
  }, [period]);

  const fetchAgentProfile = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const res = await axios.get(`${API}/agent/me?token=${token}`);
      setAgentProfile(res.data);
    } catch { /* silent */ }
  };

  const handleAgentPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }

    setPhotoUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const uploadRes = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/agent/profile?token=${token}`, { photo_url: uploadRes.data.url });
      setAgentProfile(prev => ({ ...prev, photo_url: uploadRes.data.url }));
      toast.success('Profile photo updated');
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      if (isAgent) {
        const dashRes = await axios.get(`${API}/agent/dashboard?token=${token}`);
        setDashboard(dashRes.data);
        setRecentLeads(dashRes.data.recent_leads || []);
        setUpcomingApts(dashRes.data.upcoming_appointment_list || []);
      } else {
        const [dashRes, leadsRes, aptsRes] = await Promise.all([
          axios.get(`${API}/crm/dashboard?period=${period}`),
          axios.get(`${API}/leads`),
          axios.get(`${API}/appointments?upcoming=true`)
        ]);
        setDashboard(dashRes.data);
        const allLeads = Array.isArray(leadsRes.data) ? leadsRes.data : leadsRes.data.leads || [];
        setRecentLeads(allLeads.slice(0, 8));
        setUpcomingApts(Array.isArray(aptsRes.data) ? aptsRes.data.slice(0, 5) : []);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Leads', value: dashboard?.total_leads || 0, icon: Users, color: '#D4AF37', link: '/admin/leads' },
    { title: 'New Leads', value: dashboard?.pipeline_stats?.new_lead || 0, icon: TrendingUp, color: '#3B82F6', link: '/admin/leads' },
    { title: 'Qualified', value: dashboard?.pipeline_stats?.qualified || 0, icon: Target, color: '#F59E0B', link: '/admin/leads' },
    { title: 'Under Contract', value: dashboard?.pipeline_stats?.under_contract || 0, icon: DollarSign, color: '#EC4899', link: '/admin/leads' },
    { title: 'Closed', value: dashboard?.pipeline_stats?.closed || 0, icon: BarChart3, color: '#22C55E', link: '/admin/leads' },
    { title: isAgent ? 'Deal Value' : 'Total Deal Value', value: `$${((isAgent ? (dashboard?.total_deal_value || 0) : (dashboard?.deal_values?.total || 0)) / 1000).toFixed(0)}K`, icon: DollarSign, color: '#D4AF37', link: '/admin/leads' },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
              {isAgent ? 'My Dashboard' : 'CRM Dashboard'}
            </h1>
            <p className="text-[#808080] mt-1">{isAgent ? 'Your assigned leads and appointments' : 'Overview of your sales pipeline'}</p>
          </div>
          <div className="flex bg-[#111111] border border-[#333] rounded-lg overflow-hidden" data-testid="period-filter">
            {['today', 'week', 'month', 'all'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${period === p ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'text-[#808080] hover:text-[#F5F5F0]'}`}
                data-testid={`period-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner" /></div>
        ) : (
          <>
            {/* Agent Profile Card */}
            {isAgent && agentProfile && (
              <div className="dashboard-card mb-8 flex items-center gap-5" data-testid="agent-profile-card">
                <div className="relative group cursor-pointer" onClick={() => photoRef.current?.click()} data-testid="agent-photo-upload">
                  <input type="file" ref={photoRef} onChange={handleAgentPhotoUpload} accept="image/*" className="hidden" data-testid="agent-photo-input" />
                  {agentProfile.photo_url ? (
                    <img src={agentProfile.photo_url} alt={agentProfile.name} className="w-20 h-20 rounded-full object-cover border-2 border-[#D4AF37]/40" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center border-2 border-dashed border-[#D4AF37]/30">
                      <User className="w-8 h-8 text-[#D4AF37]/50" />
                    </div>
                  )}
                  <div className="absolute inset-0 w-20 h-20 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    {photoUploading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
                  </div>
                </div>
                <div>
                  <h2 className="text-xl text-[#F5F5F0] font-medium">{agentProfile.name}</h2>
                  <p className="text-[#808080] text-sm">{agentProfile.email}</p>
                  <p className="text-[#D4AF37] text-xs mt-1">Click photo to update your profile picture</p>
                </div>
              </div>
            )}

            {/* Stat Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              {statCards.map((stat) => (
                <Link
                  key={stat.title}
                  to={stat.link}
                  className="dashboard-card hover:border-[#D4AF37]/40 transition-colors"
                  data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <stat.icon className="w-5 h-5 mb-2" style={{ color: stat.color }} />
                  <p className="text-2xl font-semibold text-[#F5F5F0]">{stat.value}</p>
                  <p className="text-[#808080] text-xs mt-1">{stat.title}</p>
                </Link>
              ))}
            </div>

            {/* Pipeline Visual + Tags */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Pipeline Stages Breakdown */}
              <div className="lg:col-span-2 dashboard-card" data-testid="pipeline-breakdown">
                <h3 className="text-lg text-[#F5F5F0] mb-4" style={{ fontFamily: 'Playfair Display' }}>Pipeline Breakdown</h3>
                <div className="space-y-3">
                  {dashboard?.pipeline_stats && Object.entries(dashboard.pipeline_stats).map(([stage, count]) => {
                    const total = dashboard.total_leads || 1;
                    const pct = total > 0 ? (count / total) * 100 : 0;
                    const stageColors = {
                      new_lead: '#3B82F6', contacted: '#8B5CF6', qualified: '#F59E0B',
                      appointment_set: '#10B981', consultation: '#06B6D4', under_contract: '#EC4899',
                      clear_to_close: '#84CC16', closed: '#22C55E', follow_up: '#6B7280', lost: '#EF4444'
                    };
                    const stageNames = {
                      new_lead: 'New Lead', contacted: 'Contacted', qualified: 'Qualified',
                      appointment_set: 'Appt Set', consultation: 'Consultation', under_contract: 'Under Contract',
                      clear_to_close: 'Clear to Close', closed: 'Closed', follow_up: 'Follow-Up', lost: 'Lost'
                    };
                    return (
                      <div key={stage} className="flex items-center gap-3">
                        <span className="text-xs text-[#808080] w-28 truncate">{stageNames[stage] || stage}</span>
                        <div className="flex-1 h-5 bg-[#1A1A1A] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${Math.max(pct, count > 0 ? 3 : 0)}%`, backgroundColor: stageColors[stage] || '#808080' }}
                          />
                        </div>
                        <span className="text-xs text-[#F5F5F0] font-semibold w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tags Breakdown */}
              <div className="dashboard-card" data-testid="tag-breakdown">
                <h3 className="text-lg text-[#F5F5F0] mb-4" style={{ fontFamily: 'Playfair Display' }}>Lead Tags</h3>
                <div className="space-y-2">
                  {dashboard?.tag_stats && Object.entries(dashboard.tag_stats).map(([tag, count]) => (
                    <div key={tag} className="flex items-center justify-between p-2 rounded bg-[#1A1A1A]">
                      <span className={`px-2 py-0.5 rounded text-xs ${TAG_COLORS[tag] || 'bg-[#333] text-[#808080]'}`}>
                        {tag.replace('_', ' ')}
                      </span>
                      <span className="text-[#F5F5F0] text-sm font-semibold">{count}</span>
                    </div>
                  ))}
                  {(!dashboard?.tag_stats || Object.keys(dashboard.tag_stats).length === 0) && (
                    <p className="text-[#808080] text-sm text-center py-4">No tagged leads yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Recent Leads */}
            <div className="dashboard-card" data-testid="recent-leads">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>Recent Leads</h3>
                <Link to="/admin/leads" className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1">
                  View Pipeline <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              {recentLeads.length === 0 ? (
                <p className="text-[#808080] text-center py-8">No leads yet. Leads from your website forms will appear here.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#D4AF37]/10">
                        <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Name</th>
                        <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Source</th>
                        <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Tags</th>
                        <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Stage</th>
                        <th className="text-left py-3 px-4 text-[#808080] text-xs uppercase tracking-wider font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentLeads.map(lead => (
                        <tr key={lead.id} className="border-b border-[#1A1A1A]">
                          <td className="py-3 px-4 text-[#F5F5F0] text-sm">{lead.name}</td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded capitalize">{lead.source?.replace('_', ' ')}</span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              {(lead.tags || []).slice(0, 2).map(t => (
                                <span key={t} className={`px-1.5 py-0.5 text-[10px] rounded ${TAG_COLORS[t] || 'bg-[#333] text-[#808080]'}`}>{t}</span>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-[#808080] text-xs capitalize">{lead.stage?.replace('_', ' ')}</td>
                          <td className="py-3 px-4 text-[#808080] text-xs">{new Date(lead.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Upcoming Appointments */}
            <div className="dashboard-card mt-6" data-testid="upcoming-appointments">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
                  <CalendarClock className="w-5 h-5 inline mr-2 text-[#D4AF37]" />
                  Upcoming Appointments
                </h3>
                <Link to="/admin/appointments" className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1">
                  View All <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              {upcomingApts.length === 0 ? (
                <p className="text-[#808080] text-center py-6 text-sm">No upcoming appointments</p>
              ) : (
                <div className="space-y-2">
                  {upcomingApts.map(apt => (
                    <div key={apt.id} className="flex items-center gap-3 p-3 bg-[#1A1A1A] rounded-lg">
                      <div className="text-center flex-shrink-0 w-12">
                        <div className="text-lg font-bold text-[#D4AF37]">{new Date(apt.appointment_date).getDate()}</div>
                        <div className="text-[10px] text-[#808080] uppercase">{new Date(apt.appointment_date).toLocaleString('en-US', { month: 'short' })}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#F5F5F0] text-sm font-medium truncate">{apt.title}</p>
                        <div className="flex gap-2 text-[10px] text-[#808080]">
                          <span>{new Date(apt.appointment_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                          {apt.lead_name && <span>with {apt.lead_name}</span>}
                          <span className="capitalize">{apt.appointment_type?.replace('_', ' ')}</span>
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 text-[10px] rounded capitalize ${apt.status === 'confirmed' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
                        {apt.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};
